import { Autocomplete, Box, Typography } from '@mui/material';
import React, { ReactNode } from 'react';
import { FieldValues, useController, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DotIcon } from '@/shared/assets/icons/dot-icon';
import { Alert } from '@/shared/components/ui/alert/alert';
import { FormErrorMessage } from '@/shared/components/ui/form/components/form-error-message/form-error-message';
import {
  AdditionalInfo,
  StyledFormControl,
} from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.styled';
import {
  TBaseSearchableSelectProps,
  TGroupedOption,
  TSelectOption,
} from '@/shared/components/ui/form/components/searchable-select/base-searchable-select.types';
import { EmptyResults } from '@/shared/components/ui/form/components/searchable-select/components/empty-results/empty-results';
import { ListboxComponent } from '@/shared/components/ui/form/components/searchable-select/components/list-box/list-box';
import { SearchableSelectTagsList } from '@/shared/components/ui/form/components/searchable-select/components/tags-list/searchable-select-tags-list';
import { TextField } from '@/shared/components/ui/form/components/text-field/text-field';
import { OverlayLoader } from '@/shared/components/ui/overlay-loader/overlay-loader';
import { MenuPaper } from '@/shared/components/ui/paper/menu-paper/menu-paper';
import { TruncatedText } from '@/shared/components/ui/truncated-text/truncated-text';

export const BaseSearchableSelect = <
  TFieldValues extends FieldValues,
  TOptionShape,
>({
  name,
  label,
  placeholder,
  disabled,
  required,
  customLabel,
  multiple,
  disableTags,
  enableTagsTooltip,
  filterSelectedTags,
  onSelectChange,
  defaultValue,
  renderNoOptions,
  getItemId,
  options,
  loading = false,
  isSelected,
  onInputChange,
  inputValue,
  hint,
}: TBaseSearchableSelectProps<TFieldValues, TOptionShape>) => {
  const { t } = useTranslation();
  const { control } = useFormContext<TFieldValues>();
  const _placeholder = placeholder ?? t('ui:select_placeholder');

  const {
    field: { onChange: onFieldChange, value, ref, ...fieldProps },
    fieldState: { error },
  } = useController({
    name,
    control,
    defaultValue,
  });

  const extractValue = (item: TGroupedOption<TOptionShape>): TOptionShape => {
    if (Object.hasOwn(item, 'value')) {
      return item.value;
    }
    return item as TOptionShape;
  };

  const handleValueChange = (
    newValue:
      | TSelectOption<TOptionShape>
      | TSelectOption<TOptionShape>[]
      | null,
    onChange: (value: TOptionShape | TOptionShape[] | null) => void,
  ) => {
    if (!newValue) {
      onChange(null);
      return;
    }

    const isMultipleSelection = Array.isArray(newValue);
    const valueToUpdate = isMultipleSelection
      ? newValue.map(extractValue)
      : extractValue(newValue);

    onChange(valueToUpdate);

    if (onSelectChange) {
      onSelectChange(value, valueToUpdate, onFieldChange);
    }
  };

  const getPlaceholder = () => {
    if (!loading) {
      return _placeholder;
    }
    return t('ui:load_options');
  };

  const renderLabel = (option: TSelectOption<TOptionShape>) => {
    if (!customLabel) {
      return <TruncatedText variant="p8" text={option.label} maxLength={32} />;
    }
    return customLabel(option);
  };

  const renderInput = (params: Object) => {
    return (
      <TextField
        {...params}
        hideOptionalLabel={required}
        label={label}
        placeholder={getPlaceholder()}
        error={!!error}
        disabled={disabled || loading}
        inputRef={ref}
        slotProps={{
          inputLabel: {
            shrink: true,
          },
        }}
      />
    );
  };

  const getSelectedOptionLabel = (
    item: TSelectOption<TOptionShape>,
  ): string => {
    const itemValue = extractValue(item);
    const option = options.find(
      (current) => getItemId(current.value) === getItemId(itemValue),
    );

    return option?.tagLabel ?? option?.label ?? String(itemValue);
  };

  const renderTags = (values: TSelectOption<TOptionShape>[]) => {
    if (disableTags) {
      return false;
    }

    const visibleValues = filterSelectedTags
      ? values.filter((item) => filterSelectedTags(extractValue(item)))
      : values;

    return (
      <SearchableSelectTagsList<TOptionShape>
        options={visibleValues.map((item) => ({
          label: getSelectedOptionLabel(item),
          value: extractValue(item),
        }))}
        enableTooltip={enableTagsTooltip}
      />
    );
  };

  const getOptionLabel = (option: TSelectOption<TOptionShape>) => {
    if (option?.label) {
      return option.label;
    }

    const label = options.find((_option) => _option.value === option)?.label;

    return label ?? '';
  };

  const getValue = () => {
    if (value) {
      return value;
    }

    if (multiple) {
      return [];
    }

    return '';
  };

  const _value = getValue() as
    | TGroupedOption<TOptionShape>
    | TGroupedOption<TOptionShape>[];

  // Text shown while the field holds no value (a sentinel option's label like
  // "No group", or abandoned typing) would have to be deleted before searching.
  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (multiple || value || !inputValue) {
      return;
    }

    onInputChange(event, '', 'clear');
  };

  const renderAdditionalInfo = (info?: string | string[] | ReactNode) => {
    if (!info) {
      return undefined;
    }

    if (typeof info === 'string' || Array.isArray(info)) {
      const additionalInfo = typeof info === 'string' ? [info] : info;
      return (
        <AdditionalInfo>
          {additionalInfo.map((info, index) => (
            <React.Fragment key={index}>
              {index > 0 && <DotIcon size="3px" />}
              {info}
            </React.Fragment>
          ))}
        </AdditionalInfo>
      );
    }

    return info;
  };

  const getNoOptionsText = () => {
    if (!renderNoOptions) {
      return <EmptyResults />;
    }

    if (typeof renderNoOptions === 'string') {
      return <Typography variant="p8">{renderNoOptions}</Typography>;
    }

    return renderNoOptions;
  };

  return (
    <StyledFormControl fullWidth error={!!error}>
      <Autocomplete
        id={`${name}-searchable-select`}
        {...fieldProps}
        options={options}
        loading={loading}
        defaultValue={defaultValue}
        disabled={disabled || (loading && options?.length === 0)}
        value={_value}
        loadingText={<OverlayLoader display={true} />}
        multiple={multiple}
        disableClearable={multiple}
        disableCloseOnSelect={multiple}
        onChange={(_, newValue) => handleValueChange(newValue, onFieldChange)}
        onFocus={handleFocus}
        isOptionEqualToValue={(option, value) => {
          return (
            getItemId(extractValue(option)) === getItemId(extractValue(value))
          );
        }}
        getOptionLabel={getOptionLabel}
        inputValue={inputValue}
        onInputChange={onInputChange}
        noOptionsText={getNoOptionsText()}
        filterOptions={(x) => x}
        clearOnBlur={false}
        slotProps={{
          paper: {
            component: MenuPaper,
            style: {
              width: 'unset',
            },
          },
          listbox: {
            component: ListboxComponent,
          },
        }}
        groupBy={(option: TGroupedOption<TOptionShape>) =>
          option.group || 'unselected'
        }
        renderGroup={(params) => params as any}
        renderTags={renderTags}
        renderOption={(props, _option, state) => {
          const { className: _, ...otherProps } = props;
          const { additionalInfo, disabled: _disabled, ...option } = _option;

          const optionProps = {
            ...otherProps,
            disabled: loading || disabled || _disabled,
            selected: isSelected(option.value),
            multiple,
            additionalInfo: renderAdditionalInfo(additionalInfo),
          };

          return [
            optionProps,
            option,
            state.index,
            renderLabel,
          ] as React.ReactNode;
        }}
        renderInput={renderInput}
        disableListWrap
      />
      {error?.message && <FormErrorMessage errorMessage={error?.message} />}
      {hint && (
        <Box margin="0.75rem 0">
          <Alert color="blue" text={hint} />
        </Box>
      )}
    </StyledFormControl>
  );
};
